import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setDurationPeriod, setIsPricePointSetFromCookie } from 'store/actions';
import {
	chosenDurationSelector,
	chosenKilometrageSelector,
	leaseCarGroupedPricePointsSelector,
} from 'store/selectors';
import { LeaseCarDuration } from 'types';
import { getValueFromCookie, setCookie } from 'utils';

export const useDefaultDuration = () => {
	const dispatch = useDispatch();
	const selectedDuration = useSelector(chosenDurationSelector);
	const selectedKilometrage = useSelector(chosenKilometrageSelector);
	const groupedPricePoints = useSelector(leaseCarGroupedPricePointsSelector);
	const durationValueFromCookie = getValueFromCookie('duration');

	const [cheapestDuration, setCheapestDuration] = useState<string>('');
	const [availableDurations, setAvailableDurations] = useState<LeaseCarDuration[]>([]);

	// Find cheapest duration
	useEffect(() => {
		if (groupedPricePoints) {
			const durations = Object.values(groupedPricePoints);
			const freeDuration = durations.find((dur) => dur.price === 0);
			if (freeDuration) {
				setCheapestDuration(freeDuration.name);
			}
			setAvailableDurations(durations);
		}
	}, [groupedPricePoints]);

	// Set default duration if not already selected
	useEffect(() => {
		if (!selectedDuration && groupedPricePoints) {
			// Check if we have a cookie value
			if (durationValueFromCookie) {
				// Validate cookie value against available durations
				const isValidDuration = Object.values(groupedPricePoints).some(
					(dur) => dur.name === durationValueFromCookie
				);

				if (isValidDuration) {
					dispatch(setDurationPeriod(durationValueFromCookie));
					// Set flag after applying cookie value
					dispatch(setIsPricePointSetFromCookie(true));
				} else if (cheapestDuration) {
					// Fall back to the cheapest duration
					dispatch(setDurationPeriod(cheapestDuration));
					setCookie('duration', cheapestDuration);
				}
			} else if (cheapestDuration) {
				// No cookie, use cheapest
				dispatch(setDurationPeriod(cheapestDuration));
				setCookie('duration', cheapestDuration);
			}
		}
	}, [selectedDuration, durationValueFromCookie, cheapestDuration, dispatch, groupedPricePoints]);

	// Sync cookie value with selected duration
	useEffect(() => {
		if (
			durationValueFromCookie &&
			selectedDuration !== durationValueFromCookie &&
			groupedPricePoints
		) {
			// Validate cookie value against available durations
			const isValidDuration = Object.values(groupedPricePoints).some(
				(dur) => dur.name === durationValueFromCookie
			);

			if (isValidDuration) {
				dispatch(setDurationPeriod(durationValueFromCookie));
			}
		}
	}, [durationValueFromCookie, selectedDuration, dispatch, groupedPricePoints]);

	return {
		selectedDuration: durationValueFromCookie || selectedDuration || cheapestDuration,
		availableDurations,
		cheapestDuration,
		groupedPricePoints,
		selectedKilometrage,
	};
};
